import { type ReactNode } from 'react'
import { Link } from 'react-router'

/**
 * Shared footer — ported from `FgFooter` in `designs/system.jsx` (~407),
 * derived from node `710:1206`. The frames draw it on every full-page artboard,
 * so it is an atom rather than a page part; the app had exactly one footer,
 * inline in `TourismHome`, and every other page ended in whitespace where the
 * design has a footer.
 *
 * WHAT WAS DROPPED, AND WHY. The artboards draw four columns. Two of them —
 * Company (About / For hospitals / Contact us) and Legal (Privacy policy /
 * Terms of service / Data protection) — named six surfaces that did not exist
 * in this product. A footer is on every page, so a dead link here is a dead
 * link everywhere; ADR-0038 is explicit that the frames do not decide what
 * exists. T04 dropped both columns whole rather than pointing them at `/`, and
 * filed the gap as a follow-up rather than hiding it.
 *
 * M17-T17 then closed it the only way a footer column should be closed: it
 * built the surfaces. Four of the six now exist and are linked. Two remain
 * dropped — "About Medcor" for want of approved copy, "For hospitals"
 * because ADR-0011 retired that audience. See `COLUMNS` below.
 *
 * The upstream "For Teams" title is not used either. ADR-0011 retired the B2B
 * corporate audience, so a column promising a teams product would advertise a
 * capability we deliberately do not have. Its two surviving links are both
 * Tourism surfaces, so the column is titled for what it actually contains —
 * naming an existing thing accurately, not inventing a new one.
 *
 * COLOUR. Upstream paints the footer from ramp steps (green-900 ground,
 * green-50 headings, green-100 links). `index.css` deliberately carries no ramp
 * names — they live in designs/tokens.css as the documented source — so each is
 * mapped to its nearest semantic token: ink-1 (#1c211d vs #112314, 0.05% apart
 * in luminance), accent-on (#eaeeea, byte-identical to green-50) and line-2 for
 * the links, which preserves upstream's two-step heading/link hierarchy at
 * 10.99:1 where green-100 gave 9.68:1.
 */

type FooterLink = { label: string; to: string }
type FooterColumn = { title: string; links: FooterLink[] }

/** Every link resolves to a route that exists in `App.tsx`. Adding one here
 *  without adding the route is the failure this component was written to
 *  avoid — `Footer.test.tsx` checks each href is a real path, not `#`. */
const COLUMNS: FooterColumn[] = [
  {
    title: 'Local',
    links: [
      { label: 'Find hospitals', to: '/' },
      { label: 'Emergency help', to: '/emergency' },
      { label: 'Pharmacies', to: '/medicines' },
    ],
  },
  {
    title: 'Tourism',
    links: [
      { label: 'Browse specialties', to: '/specialists' },
      { label: 'AMTC Concierge', to: '/tourism/enquiry' },
    ],
  },
  // M17-T17 restored these two. They are the columns T04 dropped whole rather
  // than pointing at `/`, and they came back the only way a footer column
  // should: the surfaces were built first.
  //
  // "About Medcor" is STILL DROPPED, and deliberately — there is no approved
  // About copy, and T17 is explicit that shipping one with placeholder text is
  // worse than not having it. "For hospitals" stays dropped too: that is the
  // B2B audience ADR-0011 retired, and restoring it needs an ADR, not a link.
  {
    title: 'Company',
    links: [{ label: 'Contact us', to: '/contact' }],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy policy', to: '/privacy' },
      { label: 'Terms of service', to: '/terms' },
      { label: 'Data protection', to: '/data-protection' },
    ],
  },
]

export function Footer({ className }: { className?: string }): ReactNode {
  return (
    <footer
      className={[
        'mt-auto flex flex-wrap gap-x-12 gap-y-8 bg-ink-1 px-8 py-8 text-accent-on',
        className ?? '',
      ].join(' ')}
    >
      <div className="flex min-w-52 flex-col gap-4">
        <span className="text-[20px] font-medium leading-8 text-accent-on">
          med<span className="text-line-2">cor</span>
        </span>
        {/* The frame puts three social pills here (710:1206). They are dropped:
            the product has no social accounts to point them at, and a round
            icon that goes nowhere is the same dead link as a worded one. */}
        <p className="max-w-md text-[13px] leading-relaxed text-line-2">
          Operated by the Africa Medical Tourism Council (AMTC). International
          patient enquiries are handled by the AMTC concierge team.
        </p>
      </div>

      <nav aria-label="Footer" className="flex flex-wrap gap-x-12 gap-y-8">
        {COLUMNS.map((col) => (
          <div key={col.title} className="flex min-w-28 flex-col gap-4">
            <span className="text-[16px] font-medium leading-6 text-accent-on">
              {col.title}
            </span>
            <div className="flex flex-col gap-2">
              {col.links.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-[14px] leading-5 text-line-2 no-underline hover:text-accent-on hover:underline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-on"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </footer>
  )
}
